/**
 * Wire-shaped read-model schemas for inbox threads and messages.
 *
 * Single source of truth across HTTP responses and WebSocket realtime
 * payloads. Putting them in `shared/domain/` (vs `api/domains/`) lets
 * the WebSocket payload types in `shared/websocket/payloads/conversations.payloads.ts`
 * import them without reversing the package's `api/` → `shared/`
 * dependency direction.
 *
 * Date columns serialize to ISO strings; the schemas validate strings.
 * Domain entities upstream (backend `ThreadSummary` / `ThreadMessage`
 * views) carry `Date` objects — JSON.stringify on the controller / WS
 * boundary handles the conversion implicitly.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";

/**
 * Denormalized inbox-list row. The detail pane fetches the full thread
 * separately, so this shape is intentionally narrow: customer + channel
 * + last-message preview + status flags. `lastMessagePreview` is
 * truncated to 200 chars by the mapper at the persistence boundary.
 */
/**
 * Note attached to a thread by an org user. Stored on the
 * `Chat.comments` Json[] column in legacy disk shape
 * `{content, date, userId?}`; the mapper renames `date → createdAt`
 * and normalizes the optional `userId` to `null` so the wire contract
 * reads cleanly. Disk shape stays legacy-compatible until the inbox
 * cutover so the old inbox still renders notes during parallel-run.
 */
export const ThreadNoteSchema = z.object({
  content: z.string(),
  createdAt: z.string().datetime(),
  userId: z.string().nullable()
});
export type ThreadNote = z.infer<typeof ThreadNoteSchema>;

/**
 * Synthesized status the inbox surfaces in the pane selector — collapses
 * the legacy two-column semantic (`Chat.status` × `Chat.ticketStatus`)
 * into one user-facing enum.
 *
 * Server-side mapping (write path):
 *   - `CLOSED`              → `chatStatus = ARCHIVED`, `ticketStatus` unchanged
 *   - any of the other four → `chatStatus = OPEN`, `ticketStatus = <selection>`
 *
 * Client-side derivation (read path):
 *   - `chatStatus === ARCHIVED` → `CLOSED` (always wins)
 *   - else                     → `ticketStatus`
 *
 * Lifted to `@nexus/types` so the API body schema (UpdateThreadStatus),
 * the backend write adapter, and the frontend pane / dropdown share one
 * source of truth. The four overlapping literals match
 * `DbEnum.ChatTicketStatus` values exactly so a `ChatTicketStatus`
 * value flows into a `ThreadStatusSelection` slot without conversion.
 */
export const ThreadStatusSelectionSchema = z.enum([
  "SUBMITTED",
  "IN_PROGRESS",
  "WAITING_ON_CUSTOMER",
  "RESOLVED",
  "CLOSED"
]);
export type ThreadStatusSelection = z.infer<typeof ThreadStatusSelectionSchema>;

export const ThreadSummarySchema = z.object({
  threadId: z.string().uuid(),
  /**
   * Short user-visible chat identifier (the `X45WT_MHYMT`-style
   * `Chat.nanoId`). Distinct from `threadId` which is the system UUID.
   * Nullable because legacy chats predating the nanoId column have it
   * unset.
   */
  nanoId: z.string().nullable(),
  organizationId: z.string(),

  /**
   * Linked `Customer.id` when the chat's `DeploymentSession` resolved to a
   * known Customer row, else null. Drives the right-pane "View profile"
   * deep-link to `/app/contacts/:customerId` and the past-conversations
   * query that powers the customer-context panel.
   */
  /**
   * Operator-editable thread title shown at the top of the detail
   * pane. Sourced from `Chat.topic`. Null when the thread was never
   * named — the AI auto-naming pipeline populates this for chats it
   * processes; freshly-created and never-AI'd chats stay null and
   * the pane falls back to the customer name.
   */
  topic: z.string().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  customerHandle: z.string().nullable(),
  /**
   * `Customer.primaryEmail` when a Customer row is linked, else null.
   * The mapper trims and collapses empty/whitespace-only columns to
   * null at the persistence boundary so the wire never carries a
   * blank string.
   */
  customerEmail: z.string().nullable(),
  /**
   * `Customer.primaryPhone` when a Customer row is linked, else null.
   * The pane falls back to `customerHandle` for the phone-channel trio
   * (WHATSAPP / TWILIO_SMS / TWILIO_VOICE) when this is null —
   * rendered client-side; the wire stays strict to the linked column.
   * Mapper trims and collapses blank values to null.
   */
  customerPhone: z.string().nullable(),
  /**
   * Recording asset URL extracted from
   * `DeploymentSession.callMetadata.recordingUrl` (S3 preferred,
   * Twilio URL as fallback). Drives the right-pane audio player on
   * voice-call sessions. Null on non-voice channels and on voice
   * sessions whose recording hasn't been written yet.
   */
  callRecordingUrl: z.string().nullable(),

  channelType: z.nativeEnum(DbEnum.DeploymentType).nullable(),
  deploymentId: z.string().uuid().nullable(),
  deploymentName: z.string().nullable(),

  agentId: z.string().uuid().nullable(),
  chatStatus: z.nativeEnum(DbEnum.ChatStatus),
  ticketStatus: z.nativeEnum(DbEnum.ChatTicketStatus),
  responseHandling: z.nativeEnum(DbEnum.ChatResponseHandling),

  /**
   * Per-user read-state for the calling user.
   * - HTTP responses include this populated from the caller's
   *   `ConversationReadState` row (LEFT JOIN, null when no row).
   * - WS broadcasts on the org room intentionally emit `null` because
   *   the org room can't know which user changed state. Frontend
   *   thread.created / thread.updated handlers MERGE-PRESERVE the
   *   cached `lastReadAt` across these patches:
   *     `{ ...incoming, lastReadAt: existing?.lastReadAt ?? incoming.lastReadAt }`
   *
   * The frontend derives `unread` purely client-side at the rendering
   * site as `lastReadAt === null || lastReadAt < lastMessageAt` — no
   * boolean on the wire. The legacy org-wide `unread` column on `Chat`
   * is unfit for per-user semantics and is no longer surfaced.
   */
  lastReadAt: z.string().datetime().nullable(),
  assigneeIds: z.array(z.string()),

  lastMessageAt: z.string().datetime().nullable(),
  lastMessagePreview: z.string().nullable(),

  /**
   * Highlighted snippet of the matched message body when the thread
   * surfaced via the search filter axis. Already-safe HTML — the
   * backend's snippet sanitizer allows only `<mark>...</mark>` tags
   * and escapes everything else, so the frontend renders this verbatim
   * via `dangerouslySetInnerHTML`.
   *
   * Null on every list-path response that did not run search OR on
   * search hits where the match came from topic / customer name /
   * customer identifier (not message body). Topic / customer hits
   * still return the row but without a body snippet — the frontend
   * highlights the customer name client-side via `HighlightedText`.
   *
   * The full-thread reads (`GetThread`, gateway broadcasts) always
   * emit null because they do not run the search pipeline.
   */
  matchedSnippet: z.string().nullable(),

  /**
   * Latest **inbound** message timestamp on the thread — most recent
   * `Message` row where `type === HUMAN` and `userId IS NULL` (true
   * customer message). Excludes operator interventions (HUMAN with
   * userId set) and AI replies. `null` when the thread has never
   * received an inbound message.
   *
   * Drives the WhatsApp 24-hour reply window gate in the composer:
   * disabled when `channelType === WHATSAPP` and this timestamp is
   * older than 24h. Computed server-side because the message list
   * paginates — the latest inbound row may sit outside the loaded
   * slice. Frontend keeps it fresh by bumping to the new message's
   * `createdAt` whenever a `senderRole === "customer"` ThreadMessage
   * arrives via realtime; no backend recompute on every thread emit.
   */
  lastInboundMessageAt: z.string().datetime().nullable(),

  createdAt: z.string().datetime(),
  /**
   * Bumped by every mutation that touches the chat (assignee change,
   * note added, status change, response-handling change, …) — distinct
   * from `lastMessageAt` which tracks message activity only. The
   * details pane shows both: "Last update" + "Last message".
   */
  updatedAt: z.string().datetime(),
  /**
   * Set when the chat is archived (selector "Closed"). Shown in the
   * details pane only when populated. Null for any non-closed chat.
   */
  closedAt: z.string().datetime().nullable(),
  /**
   * Notes attached to the thread by org users. Append-only in legacy;
   * the conversations rebuild keeps the same shape on disk and exposes
   * them via this field. Empty array when none — never null.
   */
  notes: z.array(ThreadNoteSchema)
});
export type ThreadSummary = z.infer<typeof ThreadSummarySchema>;

/**
 * Keyset cursor for the thread list — `{ lastMessageAt, threadId }`
 * keeps pagination stable across concurrent inserts. `lastMessageAt`
 * is null when the cursor advanced past a chat with no last-message
 * timestamp.
 */
export const ThreadCursorSchema = z.object({
  lastMessageAt: z.string().datetime().nullable(),
  threadId: z.string().uuid()
});
export type ThreadCursor = z.infer<typeof ThreadCursorSchema>;

/**
 * Single message inside a thread, shaped for the chat detail pane
 * transcript. `senderRole` collapses MessageContentType into the three
 * roles the inbox aligns by for bubble layout.
 *
 * `status` mirrors `Message.status` so the frontend can branch on
 * `WAITING_VALIDATION` (validation-workflow draft → renders the inline
 * editor) vs everything else (normal bubble). `INVALIDATED` rows stay
 * on the wire as soft-deleted records — the cache keeps the row so
 * counts + ordering stay stable across the patch.
 */
export const ThreadMessageSchema = z.object({
  messageId: z.string().uuid(),
  threadId: z.string().uuid(),
  senderRole: z.enum(["customer", "agent", "user"]),
  /**
   * Resolved-per-role display label rendered next to the bubble.
   *   - customer rows: resolved via `resolveCustomerName(deploymentType,
   *     customerIdentity)` at the persistence boundary — null when the
   *     channel-side identity has no friendly name (raw email handle on
   *     SMTP/IMAP threads with anonymous senders, etc).
   *   - agent rows: `agent.firstName + lastName` joined off the message-
   *     side `agentId`.
   *   - user rows: `user.firstName + lastName` joined off `userId`,
   *     fallback to the denormalized `Message.name` column when the
   *     User join missed.
   * Always-null is acceptable; the bubble degrades to timestamp-only.
   */
  senderName: z.string().nullable(),
  /**
   * Avatar URL rendered above the bubble (`h-6 w-6 rounded-lg`):
   *   - customer rows: `customer.avatarUrl` from the joined Customer
   *     entity when populated; null until customer-record enrichment
   *     fills it in (most channel humans pre-enrichment fall back to
   *     the initials renderer derived from `senderName`).
   *   - agent rows: `agent.profile_picture`.
   *   - user rows: always null. The User model has no avatar column;
   *     operator avatars come from Clerk runtime and are not joined on
   *     the read path. Bubble renders an initials fallback.
   */
  senderAvatarUrl: z.string().nullable(),
  textContent: z.string().nullable(),
  /**
   * Tool-call classification surfaced for transcript context. Non-null
   * on `Message.type === TOOL` rows. Drives the right-side tool chip
   * the bubble renders above (or in lieu of) the text body so
   * operators reviewing an agentic conversation see "Tool: foo" /
   * "Workflow: bar" instead of an empty agent bubble.
   *
   * The full tool-input / tool-output / workflow / source / code-
   * interpreter rich-rendering surface stays deferred to a focused
   * follow-up — this minimal chip is the cutover-floor parity.
   */
  toolType: z.nativeEnum(DbEnum.ToolType).nullable(),
  /**
   * Human-readable tool name. Sourced from `Message.name` (the
   * legacy column the chat assistant writes the function name into);
   * null when the column is empty even on a TOOL row.
   */
  toolName: z.string().nullable(),
  /**
   * Voice-call / voice-note audio asset URL extracted from
   * `Message.content.audioUrl`. Null on text-only rows. When present,
   * the bubble renders an inline audio player (with the textContent
   * surfaced as a transcript when populated by the channel ASR).
   */
  audioUrl: z.string().nullable(),
  /**
   * Image asset URLs extracted from `Message.content.images`. Channel
   * adapters populate this for inbound photos (WhatsApp, Telegram,
   * etc.). Empty array when none — never null.
   */
  imageUrls: z.array(z.string()),
  /**
   * Knowledge attachments persisted alongside the message
   * (PDFs, docs, transcripts). Wire-friendly subset of the
   * Knowledge entity — id, name, optional MIME type, size as
   * a number-coerced byte count, optional pre-signed URL.
   * Empty array when none — never null.
   */
  attachments: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      contentType: z.string().nullable(),
      sizeBytes: z.number().nullable(),
      url: z.string().nullable()
    })
  ),
  createdAt: z.string().datetime(),
  status: z.nativeEnum(DbEnum.MessageStatus),
  /**
   * `Message.agentId` — the agent that generated the row, NOT the
   * thread's current agent. Distinct because legacy workflow events
   * filter on the message-side agent. Null for system / customer rows.
   */
  agentId: z.string().nullable(),
  /**
   * Operator-submitted satisfaction rating for the message.
   *
   * Null until an operator clicks 👍 / 👎 on an outbound bubble; once
   * set, the row is one-shot (legacy parity — no remove-rating
   * affordance). `comment` is null when the operator skipped the
   * follow-up popover, populated when they typed one and submitted.
   * Score range on the conversations surface is `-1` (👎) or `1` (👍);
   * the underlying SatisfactionScore table accepts -1..10 for the
   * public API surface but conversations restricts to thumbs.
   */
  satisfaction: z
    .object({
      score: z.number(),
      comment: z.string().nullable()
    })
    .nullable(),
  /**
   * Outbound delivery state from the channel-dispatch pipeline. Drives
   * the FAILED-row badge on operator-side bubbles. Null on rows that
   * were never dispatched (customer messages, embed sessions, legacy
   * pre-dispatch-tracking rows). PENDING / SENT do not surface a badge
   * — only FAILED. `deliveryError` is the SDK error string captured by
   * the dispatch adapter when present (e.g. "Phone number not
   * registered"); shown verbatim in the badge tooltip.
   */
  deliveryStatus: z.nativeEnum(DbEnum.MessageDeliveryStatus).nullable(),
  deliveryError: z.string().nullable(),
  /**
   * Full message JSON content blob — variable-shaped per channel
   * adapter and per AI provider response shape. Carries AI-generated
   * structured content (text, code-interpreter blocks, embedded
   * reasoning marks, tool-output payloads, channel-specific metadata).
   * Forwarded unchanged from `Message.content`; the wire is opaque on
   * purpose so the rendering layer can decode whichever variant the
   * row carries without locking the wire to one provider's shape.
   * Null on rows with no JSON payload (legacy text-only customer
   * messages, plain operator replies).
   */
  content: z.unknown().nullable(),
  /**
   * Tool execution metadata — workflow status / execution id / icon,
   * source-retrieval output metadatas, running-tool display text.
   * Forwarded unchanged from `Message.statusInfos`; the wire is opaque
   * for the same reason as `content`. Null on non-tool rows or rows
   * predating tool-status tracking.
   */
  statusInfos: z.unknown().nullable(),
  /**
   * Function-call invocations the model emitted on this AI row. Each
   * entry shapes as `{ id?, function: { name, arguments } }`. Empty
   * array when the row had no tool calls (or was not an AI row).
   * Forwarded from the Prisma `Message.toolCalls` (Json[]) column;
   * the wire is `unknown[]` so the renderer narrows per-entry.
   */
  toolCalls: z.array(z.unknown())
});
export type ThreadMessage = z.infer<typeof ThreadMessageSchema>;

/**
 * Keyset cursor for paginating *older* messages within one thread.
 * Walks (createdAt DESC, messageId DESC) — the same direction as the
 * adapter so the UI can paginate upward.
 */
export const ThreadMessageCursorSchema = z.object({
  createdAt: z.string().datetime(),
  messageId: z.string().uuid()
});
export type ThreadMessageCursor = z.infer<typeof ThreadMessageCursorSchema>;
